import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Column } from "@universal-ui";
import { format } from "date-fns";
import { Subscription, ReplaySubject, switchMap, of } from "rxjs";

import mensajes from "@assets/constantes/mensajes.json";

import { DATE_FORMAT } from "@shared/Constantes/date.constants";
import { ModalTitles, ModalType } from "@shared/Constantes/modal.constantes";
import { FiltroUsuarios } from "@shared/Entities/Filtro/filtroUsuario.entity";
import { Pagination } from "@shared/Entities/Pagination/Pagination.entity";
import { Rol } from "@shared/Entities/Rol/rol.entity";
import { Usuario } from "@shared/Entities/Usuario/usuario.entity";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";
import { ModalService } from "@shared/Services/modal.service";

import { FilterTableServerComponent } from "../../General/Table/FilterTableServer/filterTableServer.component";
import { CanalesDashboardService } from "../Shared/Services/canales-dashboard.service";
import { CanalesService } from "../Shared/Services/canales.service";

export interface UsuariosResponse {
  items: Usuario[];
  pagination: Pagination;
}

@Component({
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardCanalesComponent implements OnInit, OnDestroy {
  public subscriptions: Subscription[] = [];
  public usuarios: Usuario[];
  public observableUsuarios: ReplaySubject<Usuario[]> = new ReplaySubject(1);
  public filterParameter: FiltroUsuarios;
  public selectedUsers: Usuario[] = [];
  public currentUser: Usuario;
  public serviceConsuming: string;
  public serverMode: boolean = true;
  public serverSidePagination: ReplaySubject<Pagination> = new ReplaySubject(1);

  public excelName = "Usuarios";
  @ViewChild("actions", { static: true }) public actionsTemplate;
  @ViewChild(FilterTableServerComponent<unknown>, { static: true })
  public filterTable: FilterTableServerComponent<unknown> | null;

  public resetPagination: ReplaySubject<void> = new ReplaySubject<void>();

  public showForm = false;
  public columns: Column[] = [];
  public searchCols: string[] = [
    "metadata.numeroIdentificacion",
    "metadata.nombres",
    "metadata.apellidos",
    "metadata.email",
    "perfil.nombre",
    "metadata.identificacionPerfil",
    "metadata.tipoInfo",
    "metadata.tipoInfoARS",
    "roles",
    "metadata.empresa",
    "metadata.userCode",
    "metadata.codigoIntermediario",
    "metadata.clientCode",
    "metadata.codigoPrestador",
    "metadata.sucursal",
    "metadata.telephone",
    "formattedCreado",
    "provincia",
  ];

  constructor(
    private usuarioRepository: UsuariosRepository,
    private modalService: ModalService,
    private canalesService: CanalesService,
    private router: Router,
    private readonly canalesDashboardService: CanalesDashboardService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.push(
      this.usuarioRepository.getInfoClienteActual().subscribe((currentUser) => {
        this.currentUser = currentUser;
        this.getUsuarios();
      })
    );

    this.columns = this.canalesDashboardService.setColumns(this.actionsTemplate);
  }

  public getUsuarios(spinner: boolean = true): void {
    this.usuarioRepository.getAllUsersFilter(10, 1, 10, {}, spinner).subscribe((usuarios) => {
      this.setUserData(usuarios);
    });
  }

  //Change this to filter server side
  public filterUsuarios(filterParameter: FiltroUsuarios): void {
    this.filterParameter = filterParameter;
    this.usuarioRepository.getAllUsersFilter(10, 1, 10, filterParameter).subscribe((usuarios) => {
      this.usuarios = usuarios.items.map(this.mapUser);
      this.resetPagination.next();
      this.observableUsuarios.next(this.usuarios);
      this.serverSidePagination.next({
        currentPage: usuarios.pagination.currentPage,
        pageSize: usuarios.pagination.pageSize,
        maxPage: usuarios.pagination.totalPages,
      });
    });
  }

  public onUserClicked(clickedUser: Usuario): void {
    if (clickedUser.selected) this.selectedUsers.push(clickedUser);
    else
      this.selectedUsers = this.selectedUsers.filter((user) => {
        return user.id !== clickedUser.id;
      });
  }

  public resetUserPassword(user: Usuario): void {
    this.usuarioRepository.reiniciarContrasena(user.userB2CId).subscribe(() => {
      this.selectedUsers = [];
      this.modalService.showModalGeneral({
        title: ModalTitles.success,
        body: mensajes.gestionUsuarios.contrasenaReiniciada.body,
        type: ModalType.success,
      });
    });
  }

  public deleteCPUser(user: Usuario): void {
    this.modalService
      .showConfirm({
        title: ModalTitles.warning,
        body: "¿Está seguro que desea eliminar este usuario?",
        type: ModalType.warning,
      })
      .pipe(
        switchMap((confirm) => {
          if (confirm) {
            return this.usuarioRepository.removerUsuario(user.userFbaId);
          } else {
            return of(null);
          }
        })
      )
      .subscribe((response) => {
        if (response) {
          this.modalService.showModalGeneral({
            title: ModalTitles.success,
            body: "Usuario eliminado correctamente.",
            type: ModalType.success,
          });
          this.getUsuarios();
        }
      });
  }

  public lockUser(user: Usuario): void {
    this.canalesService.lockUser(user.userB2CId).subscribe(() => {
      this.getUsuarios(false);
    });
  }

  public unlockUser(user: Usuario): void {
    this.canalesService.unLockUser(user.userB2CId).subscribe(() => {
      this.getUsuarios(false);
    });
  }

  public editUser(user: Usuario): Promise<boolean> {
    return this.router.navigateByUrl(`canales/editar-usuario/${user.userB2CId}`);
  }

  public toggleFilter(): void {
    this.showForm = !this.showForm;
  }

  public pageChange(event: Pagination): void {
    this.usuarioRepository
      .getAllUsersFilter(event.pageSize, event.currentPage, event.maxPage, this.filterParameter)
      .subscribe((response: UsuariosResponse) => {
        this.setUserData(response);
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setUserData(response: UsuariosResponse): void {
    this.usuarios = response.items.map(this.mapUser);
    this.observableUsuarios.next(this.usuarios);
    this.serverSidePagination.next({
      currentPage: response.pagination.currentPage,
      pageSize: response.pagination.pageSize,
      maxPage: response.pagination.maxPage,
    });
  }

  private mapUser(user: Usuario): Usuario {
    return {
      ...user,
      formattedCreado: format(new Date(user.creado), DATE_FORMAT),
      formattedUltimoInicioSesion: format(new Date(user.ultimoInicioSesion), DATE_FORMAT),
      roles: user.usuarioRole.map((rol) => rol?.role?.nombre) as Rol[],
    };
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) subscription.unsubscribe();
    });
  }
}
